import { ApiError } from './core'
import { config } from '../../config'
import { createServiceFetch } from '../../utils/validateFetchUrl'

// Prompt-optimization endpoints live under AIServices (/aiservices/prompt-optimization/...).
const optimizationFetch = createServiceFetch(config.api.aiservicesUrl)

/** Enhancement 5 (CoT): Prompt pattern for specialized optimization. */
export type PromptType = 'standard' | 'cot' | 'few_shot' | 'structured_output'

/** How to pass golden-dataset context into DSPy optimization. */
export type DspyContextMode = 'auto' | 'inject_field' | 'rag_module'

/** Strategy for selecting few-shot demonstrations from the golden dataset. */
export type DspyDemoSelectionStrategy = 'default' | 'diverse_context' | 'max_coverage'

export interface DspyContextConfig {
  context_mode?: DspyContextMode
  demo_selection_strategy?: DspyDemoSelectionStrategy
  retrieval_top_k?: number
  context_field_name?: string
  enable_context_summarization?: boolean
  max_context_tokens?: number
}

/** Enhancement 11: Guardrail backend for content safety filtering. */
export type GuardrailBackend = 'builtin' | 'azure_content_safety' | 'aws_bedrock' | 'custom'

export interface GuardrailConfig {
  backend?: GuardrailBackend
  backend_params?: Record<string, unknown>
  checks?: string[]
}

/** Subset of eval config sent with prompt optimization (matches AIServices EvalConfig). */
export interface OptimizationEvalConfig {
  provider?: string
  model_id?: string
  concurrency?: number
  /** When set with semantic drift guard, enables embedding-based cosine drift (else Jaccard fallback). */
  embedding_model_id?: string
  /** TAGP database connector for Text2SQL execution metrics (EX, VES). */
  sql_connector_id?: string
  /** Advanced SQLAlchemy URL override for Text2SQL execution metrics. */
  sql_connection_string?: string
  /** Declared schema JSON for text2sql_schema_compliance. */
  schema_definition?: Record<string, unknown> | unknown[] | string
}

export interface OptimizationMetricConfigEntry {
  metric_key: string
  framework: 'deepeval' | 'ragas' | 'AgentX'
  template_id?: string
  threshold?: number
}

export interface OptimizationConfig {
  prompt_id: string
  prompt_content?: string
  framework: string
  /** Omit or leave empty for GEPA when no benchmark dataset is used. */
  dataset_id?: string
  /** Enhancement 5: CoT / few-shot / structured_output–specific strategies. */
  prompt_type?: PromptType
  dataset_config?: Record<string, unknown>
  optimizer_params?: Record<string, unknown>
  eval_metrics?: string[]
  metrics_config?: OptimizationMetricConfigEntry[]
  eval_config?: OptimizationEvalConfig
  /** Enhancement 4: Recommend metrics from dataset/prompt when true. */
  auto_discover_metrics?: boolean
  /** Enhancement 4: Weights for composite score (e.g. { "answer_correctness": 0.5, "faithfulness": 0.3 }). */
  metric_weights?: Record<string, number>
  foundation_model_id?: string
  /** Enhancement 8: When set with 2+ IDs, evaluation runs against all models and aggregates per multi_model_strategy. */
  foundation_model_ids?: string[]
  /** How to aggregate per-model scores: min (worst-case), mean (average), pareto (balance min and spread). */
  multi_model_strategy?: 'min' | 'mean' | 'pareto'
  judge_model_id?: string
  max_iterations?: number
  /** Rows per candidate in inner eval loops (optional; defaults from dataset max_rows). */
  eval_rows_per_iteration?: number
  enable_early_stopping?: boolean
  patience?: number
  /** Alias for patience (early stopping rounds without improvement). */
  early_stopping_rounds?: number
  /** When set, overrides dataset_config.max_rows for this run. */
  max_dataset_rows?: number
  time_budget_sec?: number
  target_score?: number
  max_cost_usd?: number
  /** Meta-learning warm start (Enhancement 6). */
  enable_warm_start?: boolean
  /** Adversarial robustness testing (Enhancement 9). */
  enable_adversarial_testing?: boolean
  adversarial_weight?: number
  /** Semantic drift guard: reject candidates that drift too far from original (Enhancement 6). */
  enable_drift_guard?: boolean
  /** Max allowed drift (0–1); candidates with 1 - cosine_similarity > max_drift are rejected. */
  max_drift?: number
  /** How drift is computed when enable_drift_guard is true: jaccard (token) or vector (embedding). */
  drift_guard_method?: 'jaccard' | 'vector'
  /** Required when drift_guard_method is vector; registered embedding model id. */
  drift_embedding_model_id?: string
  /** Require human approval before persisting optimized prompt (Enhancement 15). */
  require_approval?: boolean
  /** Enhancement 11: Content safety guardrails (toxicity, PII, prompt injection, brand safety). */
  enable_guardrails?: boolean
  guardrail_checks?: string[]
  guardrail_config?: GuardrailConfig | null
  /** Enhancement 12: Session priority (low | normal | high); high may use dedicated task queue. */
  priority?: 'low' | 'normal' | 'high'
  /** Enhancement 12: Set true to acknowledge estimated cost when above confirmation threshold. */
  confirm_estimated_resources?: boolean
  /** Enhancement 7: Ensemble — optimizers to run in parallel before fusion (requires framework `ensemble`). */
  ensemble_frameworks?: string[]
  /** Enhancement 7: How ensemble results are combined: tournament, merge, or voting. */
  ensemble_fusion?: 'tournament' | 'merge' | 'voting'
  /**
   * When set with prompt_bundle, selected-node optimization still evaluates the full
   * agent graph (not the isolated node prompt).
   */
  agent_id?: string
  /** Selected node whose prompt is being optimized; overlays land in prompt_bundle[target_node_id]. */
  target_node_id?: string
  /** All node_id → current prompt mappings forwarded as Agentserve prompt_overrides. */
  prompt_bundle?: Record<string, string>
}

// export interface CreateOptimizationRequest extends OptimizationConfig { }

/** Filters for listing prompt optimization sessions (aligned with agent optimization list API). */
export interface PromptOptimizationSessionListFilters {
  prompt_id?: string
  search?: string
  status?: string
  approval_status?: string
  /** Optimization framework id (stored as `framework` on the session). */
  optimizer?: string
  sort_by?: string
  sort_order?: string
  skip?: number
  limit?: number
}

/** Enhancement 12: Resource estimate before starting a run. */
export interface ResourceEstimateResponse {
  estimated_llm_calls: number
  estimated_cost_usd_low: number
  estimated_cost_usd_high: number
  max_llm_calls_per_session: number
  within_llm_cap: boolean
  monthly_budget_usd?: number | null
  monthly_used_usd?: number | null
  monthly_limit_usd?: number | null
  confirmation_threshold_usd?: number | null
  requires_confirmation: boolean
}

/** Per-dataset-row snapshot from the optimized prompt evaluation (optimizer_metadata.row_evaluations). */
export interface OptimizationRowEvaluation {
  row_index: number
  question: string
  expected_output?: string | null
  generated_answer: string
  metrics: Record<string, number>
  score: number
  reasoning_by_metric: Record<string, string>
  error?: string | null
}

export interface OptimizationSessionResponse {
  id: string
  run_id?: string
  job_id?: string | null
  tenant_id: string
  user_id: string
  created_by_name?: string | null
  prompt_id: string
  prompt_name?: string | null
  framework: string
  config: Record<string, unknown>
  status: string
  started_at?: string
  completed_at?: string
  created_at?: string
  updated_at?: string
  original_score?: number
  optimized_score?: number
  improvement_pct?: number
  /** Enhancement 12: True score on holdout set (reduces overfitting inflation). */
  holdout_score?: number | null
  iterations_completed?: number
  total_evaluations?: number
  optimized_prompt_version_id?: string | null
  error_summary?: string
  optimization_trace?: Record<string, unknown>[]
  candidate_prompts?: Record<string, unknown>[]
  optimizer_metadata?: Record<string, unknown>
  optimized_components?: Record<string, unknown> | null
  workflow_id?: string
  duration_sec?: number | null
  approval_status?: string | null
  approved_by?: string | null
  approved_at?: string | null
  /** Multi-model optimization: score per foundation_model_id */
  per_model_scores?: Record<string, number> | null
  /** Row-level metric breakdown (detail API); also under optimizer_metadata.row_evaluations */
  row_evaluations?: OptimizationRowEvaluation[]
  /** How row_evaluations were produced: optimization_trace | final_eval | none | failed */
  row_evaluations_source?: string | null
  /** Link to this run in the orchestrator UI (e.g. Temporal Web). */
  orchestrator_run_url?: string | null
  /** Total prompt tokens consumed during optimization */
  total_prompt_tokens?: number
  /** Total completion tokens consumed during optimization */
  total_completion_tokens?: number
  /** Estimated cost in USD */
  estimated_cost_usd?: number
}

export interface OptimizationSessionListPayload {
  items: OptimizationSessionResponse[]
  total: number
}

export interface ApprovalAction {
  action: 'approve' | 'reject'
  reason?: string
}

export interface ApprovalResult {
  status: string
  session_id: string
}

export interface BatchApprovalItem {
  session_id: string
  action: 'approve' | 'reject'
  reason?: string
}

export interface BatchApprovalResultItem {
  session_id: string
  action: string
  status: string
  error?: string | null
}

export interface BatchApprovalResponse {
  results: BatchApprovalResultItem[]
  approved_count: number
  rejected_count: number
  failed_count: number
}

export interface OptimizerRegistryMeta {
  framework_name: string
  framework_version: string
  supported_params: Record<string, unknown>
  /** When false, the optimizer plugin is not installed or unavailable on the server. */
  available?: boolean
}

/** Defaults for optimization UI so judge model is chosen at trigger time (not server env). */
export interface OptimizationDefaults {
  default_judge_model_id: string | null
  default_judge_model_label: string
  default_judge_model_sentinel: string
}

/** Partial-success result from POST /sessions/bulk-delete. */
export interface BulkDeleteSessionsResult {
  deleted: number
  skipped: number
  errors: string[]
}

/** Request for A/B test spec generation (Enhancement 7). */
export interface GenerateAbTestRequest {
  expected_daily_traffic?: number
  alpha?: number
  power?: number
}

/** A/B test spec returned by generate-ab-test (Enhancement 7). */
export interface AbTestSpec {
  variants: {
    control: { name: string; label: string; prompt: string }
    treatment: { name: string; label: string; prompt: string }
  }
  baseline_score: number
  optimized_score: number
  improvement_pct: number
  improvement_pct_confidence_interval?: {
    lower_pct: number
    upper_pct: number
    confidence: number
  }
  statistical_config: {
    alpha: number
    power: number
    mde: number
    sample_size_per_variant: number
  }
  sample_size_per_variant: number
  total_sample_size: number
  recommendation: string
  recommended_test_duration_days?: number
  expected_daily_traffic?: number
  recommendation_duration?: string
}

const { auth: authConfig } = config

/** Reusable request helper for the AIServices optimization endpoints. */
async function optimizationRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const baseUrl = config.api.aiservicesUrl
  const url = `${baseUrl}/prompt-optimization${endpoint}`

  let token: string | null = null
  try {
    token = localStorage.getItem(authConfig.tokenKey)
  } catch {
    // Continue without token
  }

  let tenantId: string | null = null
  try {
    tenantId = localStorage.getItem('tagp-tenant-id')
  } catch {
    // Ignore
  }

  const { headers: optionHeaders, ...restOptions } = options
  const fetchConfig: RequestInit = {
    ...restOptions,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
      ...(optionHeaders || {}),
    },
  }

  const response = await optimizationFetch(url, fetchConfig)

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const detail = errorData?.detail
    const errorMessage =
      typeof detail === 'string' ? detail : `Request failed with status ${response.status}`
    throw new ApiError(errorMessage, response.status, errorData)
  }

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  if (!text || text.trim() === '') {
    return undefined as T
  }
  return JSON.parse(text) as T
}

interface OptimizationProgressEvent {
  type: string
  iteration?: number
  max_iter?: number
  best_score?: number
  status?: string
}

function processOptimizationSseLine(
  line: string,
  callbacks: { onMessage: (event: OptimizationProgressEvent) => void; onDone?: () => void },
): void {
  if (!line.startsWith('data: ')) return
  try {
    const data = JSON.parse(line.slice(6)) as OptimizationProgressEvent
    callbacks.onMessage(data)
    if (data.type === 'done') callbacks.onDone?.()
  } catch {
    // ignore parse errors
  }
}

async function readOptimizationSseStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  decoder: TextDecoder,
  callbacks: { onMessage: (event: OptimizationProgressEvent) => void; onDone?: () => void },
): Promise<void> {
  let buffer = ''
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''
      for (const line of lines) {
        processOptimizationSseLine(line, callbacks)
      }
    }
  } finally {
    reader.releaseLock()
  }
}

export const optimizationApi = {
  startSession: (
    data: CreateOptimizationRequest,
    options?: { idempotencyKey?: string },
  ): Promise<OptimizationSessionResponse> =>
    optimizationRequest<OptimizationSessionResponse>('/sessions', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        ...(options?.idempotencyKey
          ? { 'Idempotency-Key': options.idempotencyKey }
          : {}),
      },
    }),

  /** Enhancement 12: Estimate LLM calls and cost before starting; for confirmation step. */
  estimateResources: (data: CreateOptimizationRequest): Promise<ResourceEstimateResponse> =>
    optimizationRequest<ResourceEstimateResponse>('/estimate', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getDefaults: (): Promise<OptimizationDefaults> =>
    optimizationRequest<OptimizationDefaults>('/defaults'),

  getSession: (id: string): Promise<OptimizationSessionResponse> =>
    optimizationRequest<OptimizationSessionResponse>(`/sessions/${id}`),

  listSessions: (
    prompt_id?: string,
    skip = 0,
    limit = 50,
    search?: string,
    filters?: Omit<PromptOptimizationSessionListFilters, 'prompt_id' | 'skip' | 'limit' | 'search'>,
  ): Promise<OptimizationSessionListPayload> => {
    const params = new URLSearchParams()
    if (prompt_id) params.append('prompt_id', prompt_id)
    params.append('skip', skip.toString())
    params.append('limit', limit.toString())
    if (search?.trim()) params.append('search', search.trim())
    const f = filters
    if (f?.status) params.append('status', f.status)
    if (f?.approval_status) params.append('approval_status', f.approval_status)
    if (f?.optimizer) params.append('optimizer', f.optimizer)
    if (f?.sort_by) params.append('sort_by', f.sort_by)
    if (f?.sort_order) params.append('sort_order', f.sort_order)
    return optimizationRequest<OptimizationSessionListPayload>(`/sessions?${params.toString()}`)
  },

  cancelSession: (id: string): Promise<void> =>
    optimizationRequest<void>(`/sessions/${id}/cancel`, { method: 'POST' }),

  retrySession: (id: string): Promise<OptimizationSessionResponse> =>
    optimizationRequest<OptimizationSessionResponse>(`/sessions/${id}/retry`, { method: 'POST' }),

  deleteSession: (id: string): Promise<void> =>
    optimizationRequest<void>(`/sessions/${id}`, { method: 'DELETE' }),

  bulkDeleteSessions: (sessionIds: string[]): Promise<BulkDeleteSessionsResult> =>
    optimizationRequest<BulkDeleteSessionsResult>('/sessions/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ session_ids: sessionIds }),
    }),

  /**
   * Enhancement 15: Open SSE stream for real-time progress. Calls onMessage for each event.
   * Returns an AbortController so the caller can abort the stream. Falls back to polling if stream fails.
   */
  streamSessionProgress: (
    sessionId: string,
    callbacks: { onMessage: (event: { type: string; iteration?: number; max_iter?: number; best_score?: number; status?: string }) => void; onDone?: () => void }
  ): AbortController => {
    const baseUrl = config.api.aiservicesUrl
    const url = `${baseUrl}/prompt-optimization/sessions/${sessionId}/stream`
    let token: string | null = null
    let tenantId: string | null = null
    try {
      token = localStorage.getItem(authConfig.tokenKey)
      tenantId = localStorage.getItem('tagp-tenant-id')
    } catch {
      // ignore
    }
    const controller = new AbortController()
    optimizationFetch(url, {
      signal: controller.signal,
      headers: {
        Accept: 'text/event-stream',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(tenantId && { 'X-Tenant-ID': tenantId }),
      },
    })
      .then(async (res) => {
        if (!res.ok || !res.body) return
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        await readOptimizationSseStream(reader, decoder, callbacks)
      })
      .catch(() => {
        // SSE failed; caller can rely on polling
      })
    return controller
  },

  /** Generate A/B test spec with power analysis and optional duration (Enhancement 7). */
  generateAbTest: (
    sessionId: string,
    body?: GenerateAbTestRequest
  ): Promise<AbTestSpec> =>
    optimizationRequest<AbTestSpec>(`/sessions/${sessionId}/generate-ab-test`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),

  getFrameworks: async (): Promise<Record<string, OptimizerRegistryMeta>> => {
    const raw = await optimizationRequest<
      | Record<string, OptimizerRegistryMeta>
      | Array<{ key: string; display_name: string; available?: boolean }>
    >('/frameworks')
    if (Array.isArray(raw)) {
      const map: Record<string, OptimizerRegistryMeta> = {}
      for (const item of raw) {
        map[item.key] = {
          framework_name: item.display_name,
          framework_version: '',
          supported_params: {},
          ...(item.available === false ? { available: false } : { available: true }),
        }
      }
      return map
    }
    return raw
  },

  approveSession: (
    sessionId: string,
    action: ApprovalAction
  ): Promise<ApprovalResult> =>
    optimizationRequest<ApprovalResult>(`/sessions/${sessionId}/approve`, {
      method: 'POST',
      body: JSON.stringify(action),
    }),

  /** Enhancement 16: Compare 2+ sessions; returns full session details for trajectories and diff. */
  compareSessions: (sessionIds: string[]): Promise<OptimizationSessionResponse[]> =>
    optimizationRequest<OptimizationSessionResponse[]>(`/compare`, {
      method: 'POST',
      body: JSON.stringify({ session_ids: sessionIds }),
    }),

  listPendingApprovals: (promptId?: string): Promise<OptimizationSessionResponse[]> => {
    const params = new URLSearchParams()
    if (promptId) params.append('prompt_id', promptId)
    const qs = params.toString()
    const querySuffix = qs ? `?${qs}` : ''
    return optimizationRequest<OptimizationSessionResponse[]>(
      `/sessions/pending-approval${querySuffix}`
    )
  },

  batchApprove: (
    items: BatchApprovalItem[]
  ): Promise<BatchApprovalResponse> =>
    optimizationRequest<BatchApprovalResponse>('/sessions/batch-approve', {
      method: 'POST',
      body: JSON.stringify({ items }),
    }),
}
